import Breadcrumb from "components/Common/Breadcrumb"
import Loader from "components/Loader/loader"
import PermittedComponent from "PermittedComponent"
import { usePermissionData } from "AppContext"
import React, { useEffect, useMemo, useState } from "react"
import {
  FaChevronCircleLeft,
  FaChevronCircleRight,
  FaEdit,
  FaPlus,
  FaList,
} from "react-icons/fa"
import { MdDelete } from "react-icons/md"
import {
  Card,
  CardBody,
  Table,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
} from "reactstrap"
import Swal from "sweetalert2"
import {
  fetchData,
  FetchDeleteData,
  FetchPostData,
  FetchPutData,
} from "utils/Common_api_functions"

const OtherLocation = () => {
  const { checkAccess } = usePermissionData()
  const [locations, setLocations] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [modal, setModal] = useState(false)
  const [currentLocation, setCurrentLocation] = useState({
    id: null,
    name: "",
    address: "",
  })

  const [perPageItem, setPerPageItem] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)

  const [viewMode, setViewMode] = useState("other")

  useEffect(() => {
    fetchOtherLocations()
  }, [])

  async function fetchOtherLocations() {
    setViewMode("other")
    setCurrentPage(1)
    setSearchTerm("")
    const APIdata = await fetchData(setLoading, "location/fetch/others")
    if (APIdata?.success) {
      setLocations(APIdata?.data?.reverse())
    } else {
      setLocations(false)
      Swal.fire(
        "Something Went Wrong",
        APIdata?.response?.data?.message,
        "error"
      )
    }
  }

  async function fetchExistingLocations() {
    setViewMode("existing")
    setCurrentPage(1)
    setSearchTerm("")
    const APIdata = await fetchData(setLoading, "location/existing")
    if (APIdata?.success) {
      setLocations(APIdata?.data || [])
    } else {
      Swal.fire("Something Went Wrong", APIdata?.response?.data?.message, "error")
    }
  }

  const toggleModal = () => setModal(!modal)

  const handleInputChange = e => {
    const { name, value } = e.target
    setCurrentLocation({ ...currentLocation, [name]: value })
  }

  const handleAddEditLocation = async () => {
    if (!currentLocation.name.trim() || !currentLocation.address.trim()) {
      Swal.fire(
        "Validation Error",
        "Location Name and Address are required.",
        "warning"
      )
      return
    }
    if (currentLocation.id) {
      const APIdata = await FetchPutData(
        setLoading,
        "location",
        currentLocation?.id,
        currentLocation
      )
      if (APIdata?.data?.success) {
        Swal.fire(
          "Location Upadated Successfully.",
          APIdata?.data?.message,
          "success"
        )
        fetchOtherLocations()
      } else {
        Swal.fire(
          "Something Went Wrong",
          APIdata?.response?.data?.message,
          "error"
        )
      }
    } else {
      const APIdata = await FetchPostData(
        setLoading,
        "location/add/others",
        currentLocation
      )
      if (APIdata?.data?.success) {
        Swal.fire(
          "Location Added Successfully.",
          APIdata?.data?.message,
          "success"
        )
        fetchOtherLocations()
      } else {
        Swal.fire(
          "Something Went Wrong",
          APIdata?.response?.data?.message,
          "error"
        )
      }
    }
    setCurrentLocation({
      id: null,
      name: "",
      address: "",
    })
    toggleModal()
  }

  const handleDeleteLocation = id => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async result => {
      if (result.isConfirmed) {
        const APIdata = await FetchDeleteData(setLoading, "location", id)
        if (APIdata?.data?.success) {
          Swal.fire(
            "Location Deleted Successfully.",
            APIdata?.data?.message,
            "success"
          )
          fetchOtherLocations()
        } else {
          Swal.fire(
            "Something Went Wrong",
            APIdata?.response?.data?.message,
            "error"
          )
        }
      }
    })
  }

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }

  const filteredLocations = useMemo(() => {
    if (!locations || !Array.isArray(locations)) return []
    const term = searchTerm?.toLowerCase() || ""
    return locations.filter(location =>
      location?.name?.toLowerCase().includes(term) ||
      location?.address?.toLowerCase().includes(term) ||
      (viewMode === "existing" && location?.city?.toLowerCase().includes(term))
    )
  }, [locations, searchTerm, viewMode])

  const totalPages = Math.ceil(filteredLocations.length / perPageItem)

  const paginatedLocations = useMemo(() => {
    const start = (currentPage - 1) * perPageItem
    return filteredLocations.slice(start, start + perPageItem)
  }, [filteredLocations, currentPage, perPageItem])

  return (
    <PermittedComponent isAccess={checkAccess(5, 1) || checkAccess(5, 2)}>
    <div className="page-content">
      {loading && <Loader />}
      <Breadcrumb
        title="Dashboard"
        breadcrumbItem="Other Locations"
        description="Manage Other Location Details From Here"
      />

      <Card>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex gap-3 align-items-center">
              <Input
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={handleSearchChange}
                className=" w-auto"
              />
              <select
                className="form-select rounded-4 ps-3 pe-5 w-auto"
                value={perPageItem}
                onChange={e => {
                  setPerPageItem(Number(e.target.value))
                  setCurrentPage(1)
                }}
              >
                <option value={10}>Show 10</option>
                <option value={20}>Show 20</option>
                <option value={50}>Show 50</option>
              </select>
            </div>
            <div className="d-flex gap-2">
              <Button
                className={viewMode === "existing" ? "btn-blue" : "btn-secondary"}
                onClick={fetchExistingLocations}
              >
                <FaList /> Existing Location
              </Button>
              <Button
                className={viewMode === "other" ? "btn-blue" : "btn-secondary"}
                onClick={fetchOtherLocations}
              >
                <FaList /> Other Location
              </Button>
              {checkAccess(5, 3) && (
              <Button
                className="btn-blue"
                onClick={() => {
                  setCurrentLocation({
                    id: null,
                    name: "",
                    address: "",
                  })
                  toggleModal()
                }}
              >
                <FaPlus /> Add Location
              </Button>
              )}
            </div>
          </div>

          <Table style={{ tableLayout: "fixed", width: "100%" }}>
            <thead className="bg-blue">
              <tr>
                <th style={{ width: "5%" }}>#</th>
                <th style={{ width: "20%" }}>Location Name</th>
                <th style={{ width: "60%" }}>Location Address</th>
                {viewMode === "existing" && <th style={{ width: "10%" }}>City</th>}
                {viewMode === "existing" && <th style={{ width: "5%" }}>Postal Code</th>}
                {viewMode !== "existing" && <th style={{ width: "15%" }}>Action</th>}
              </tr>
            </thead>
            <tbody>
              {paginatedLocations.map((location, index) => (
                <tr key={location.id}>
                  <td> {(currentPage - 1) * perPageItem + index + 1}</td>
                  <td className="text-wrap-safe">{location.name}</td>
                  <td className="text-wrap-safe">{location.address}</td>
                  {viewMode === "existing" && <td>{location.city || "--"}</td>}
                  {viewMode === "existing" && <td>{location.postalCode || "--"}</td>}
                  {viewMode !== "existing" && (
                  <td>
                    {checkAccess(5, 4) && (
                    <Button
                      color="warning"
                      onClick={() => {
                        setCurrentLocation(location)
                        toggleModal()
                      }}
                    >
                      <FaEdit />
                    </Button>
                    )}
                    {checkAccess(5, 5) && (
                    <Button
                      color="danger"
                      onClick={() => handleDeleteLocation(location.id)}
                      className="ms-2"
                    >
                      <MdDelete />
                    </Button>
                    )}
                  </td>
                  )}
                </tr>
              ))}
              {paginatedLocations.length === 0 && (
                <tr>
                  <td colSpan={viewMode === "existing" ? 5 : 4} className="text-center">
                    No {viewMode === "existing" ? "existing" : "other"} locations found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          <div className="d-flex justify-content-between  gap-2">
            <p className="mb-0">
              Showing {paginatedLocations.length} out of{" "}
              {Array.isArray(locations) ? locations.length : 0}{" "}
              {viewMode === "existing" ? "Existing" : "Other"} Locations.
            </p>
            <div>
              <Button
                size="sm"
                disabled={currentPage === 1}
                className="fs-6"
                onClick={() => setCurrentPage(prev => prev - 1)}
              >
                <FaChevronCircleLeft />
              </Button>
              {Array.from({ length: totalPages }, (_, i) => (
                <Button
                  key={i}
                  size="sm"
                  className="mx-2 fs-6"
                  color={currentPage === i + 1 ? "primary" : "light"}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </Button>
              ))}
              <Button
                size="sm"
                className="fs-6"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => prev + 1)}
              >
                <FaChevronCircleRight />
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Modal for Add/Edit */}
      <Modal isOpen={modal} toggle={toggleModal} centered>
        <ModalHeader toggle={toggleModal}>
          {currentLocation.id ? "Edit Location" : "Add Location"}
        </ModalHeader>
        <ModalBody>
          <Label>Location Name</Label>
          <Input
            type="text"
            name="name"
            value={currentLocation.name}
            onChange={handleInputChange}
            placeholder="Location Name"
            className="mb-2"
          />
          <Label className="mt-4">Location Address</Label>
          <textarea
            type="text"
            name="address"
            value={currentLocation.address}
            onChange={handleInputChange}
            placeholder="Address"
            className="mb-2 rounded-4 form-control"
          />
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleModal}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleAddEditLocation}>
            {currentLocation.id ? "Update" : "Add"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
    </PermittedComponent>
  )
}

export default OtherLocation
